import React from 'react';
import PropTypes from 'prop-types';
import ErrorCatcher from '@fogia/core-web/components/ErrorCatcher';
import { toSnakeCase } from '@fogia/core-web/utils/string';

/**
 * Accepts raw module data from the cms and tries to match the data to a key inside availableContent.
 * @param {object} availableContent - A object with avalible content as key and the corresponding Component as value.
 * @param {object} data - A object with block/module/type data.
 * @param {...*} rest - All additional props will be forwarded to the Component.
 */

const ContentComponent = ({ availableContent, data, ...rest }) => {
    // Support both _type (Sanity) and type (legacy)
    const type = data?.type || data?._type;

    if (!type) {
        console.warn(`ContentComponent name/type could not be found not found.`);
        return null;
    }

    // Blocks use snake_case keys; Sanity may send camelCase _type – resolve to key that exists in registry.
    const normalizeType = availableContent[type] ? type : toSnakeCase(type);

    if (!availableContent?.[normalizeType]) {
        console.warn(`ContentComponent ${type} not found.`);
        return null;
    }

    const componentData = { ...data };

    const ContentComponent = availableContent[normalizeType];

    return (
        <ErrorCatcher>
            <ContentComponent type={type} data={componentData} {...rest} />
        </ErrorCatcher>
    );
};

ContentComponent.propTypes = {
    availableContent: PropTypes.object.isRequired,
    data: PropTypes.object,
};

/**
 * Accepts raw module data from the cms and loops through it. It will pass all necessary data to the ContentComponent on each itteration.
 * @param {object} availableContent - A object with avalible content as key and the corresponding Component as value.
 * @param {object[]} data - An array of objects with block/module/type data.
 * @param {...*} rest - All additional props will be forwarded to the Component.
 */

const ContentComponents = ({ availableContent, data, eagerImageModuleCount = 0, ...rest }) =>
    data?.map((item, index) => {
        const imageLoading = index < eagerImageModuleCount ? 'eager' : 'lazy';

        return (
            <ContentComponent
                availableContent={availableContent}
                key={index}
                data={item}
                first={index === 0}
                last={index === data.length - 1}
                imageLoading={imageLoading}
                {...rest}
            />
        );
    });

ContentComponents.propTypes = {
    availableContent: PropTypes.object.isRequired,
    data: PropTypes.array,
    eagerImageModuleCount: PropTypes.number,
};

export { ContentComponent, ContentComponents };
